import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useState } from 'react'

import { ProviderTaskType } from '../../../providers/ProviderTask/ProviderTask.provider'

interface CreateProviderTaskTypeModalProps {
  showCreateTypeModal: boolean
  setShowCreateTypeModal: React.Dispatch<React.SetStateAction<boolean>>
  setTaskTypes: React.Dispatch<React.SetStateAction<ProviderTaskType[]>>
  onCreateTaskType: (type: string) => Promise<any>
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const CreateProviderTaskTypeModal = ({
  showCreateTypeModal,
  setShowCreateTypeModal,
  setTaskTypes,
  onCreateTaskType,
  loading,
  setLoading,
}: CreateProviderTaskTypeModalProps) => {
  const [newTypeName, setNewTypeName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleClose = () => {
    setShowCreateTypeModal(false)
    setNewTypeName('')
    setError(null)
  }

  const handleCreateTaskType = async () => {
    if (!newTypeName.trim()) return

    try {
      setLoading(true)
      setError(null)
      const created = await onCreateTaskType(newTypeName)

      if (!created?.id) {
        setError('Failed to create task type. It may already exist.')
        return
      }
      setTaskTypes((prev) =>
        [...prev, created].sort((a, b) => a.type.localeCompare(b.type))
      )
      handleClose()
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Failed to create task type. It may already exist.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={showCreateTypeModal}
      onClose={() => setShowCreateTypeModal(false)}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle>Create New Task Type</DialogTitle>
      <DialogContent>
        <TextField
          label='Task Type Name'
          value={newTypeName}
          onChange={(e) => {
            setNewTypeName(e.target.value)
            if (error) setError(null)
          }}
          size='small'
          fullWidth
          sx={{ mt: 1 }}
          error={!!error}
        />
        {error && (
          <Alert severity='error' sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setShowCreateTypeModal(false)}>Cancel</Button>
        <Button
          variant='contained'
          onClick={handleCreateTaskType}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color='secondary' />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
